import React, { useState, useEffect } from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone, RouteSharp } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazyListWarehousingQuery } from "store/services/warehousingApi";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import TransitBulkRoutesModalForm from "./WarehousingComponents/WarehousingBulkShipment";

const WarehousingListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Warehousing" }];

  const warehousingTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Warehousing ID",
      selector: (row) => row?.warehousingId,
      cell: (row) => {
        const displayId = row?.warehousingId;
        const path = `/app/crm/warehousing/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },

    {
      name: "Warehouse",
      selector: (row) =>
        `${row?.warehouse?.warehouse_id ?? ""} - ${row?.warehouse?.name ?? ""}`,
      cell: (row) =>
        `${row?.warehouse?.warehouse_id ?? ""} - ${row?.warehouse?.name ?? ""}`,
    },
    {
      name: "Shipments",
      selector: (row) => row.shipments,
      cell: (row) => {
        const formatShipmentId = (shipmentId) =>
          shipmentId.replace(/(.{4})/g, "$1-").slice(0, -1);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [anchorEl, setAnchorEl] = useState(null);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [selectedShipments, setSelectedShipments] = useState([]);

        const shipments = Array.isArray(row.shipments) ? row.shipments : [];
        const visibleShipments = shipments.slice(0, 2);
        const remainingCount = shipments.length - visibleShipments.length;

        const handleChipClick = (event, allShipments) => {
          setAnchorEl(event.currentTarget);
          setSelectedShipments(allShipments);
        };

        const handleClose = () => setAnchorEl(null);

        const open = Boolean(anchorEl);

        return shipments.length > 0 ? (
          <>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
              {visibleShipments.map((shipment, index) => (
                <span key={index} style={{ fontSize: "0.9rem" }}>
                  {shipment.IsReturning
                    ? `${formatShipmentId(shipment.shipmentId)} (R)`
                    : formatShipmentId(shipment.shipmentId)}{" "}
                  {index !== visibleShipments.length - 1 && ","}
                </span>
              ))}
              {remainingCount > 0 && (
                <Tooltip title="Click to view all Shipment IDs">
                  <Chip
                    label={`+${remainingCount}`}
                    size="small"
                    sx={{
                      marginLeft: "4px",
                      height: "18px",
                      fontSize: "10px",
                      backgroundColor: "#e0e0e0",
                      color: "#333",
                      cursor: "pointer",
                    }}
                    onClick={(e) => handleChipClick(e, shipments)}
                  />
                </Tooltip>
              )}
            </Box>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <Box p={2} minWidth={100}>
                {selectedShipments.map((shipment, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {shipment.IsReturning
                      ? `${formatShipmentId(shipment.shipmentId)} (R)`
                      : formatShipmentId(shipment.shipmentId)}{" "}
                  </Typography>
                ))}
              </Box>
            </Popover>
          </>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Name",
      selector: (row) => row?.name,
      cell: (row) => row?.name,
    },
    {
      name: "Created By",
      selector: (row) => row.createdBy?.name || "N/A",
      cell: (row) => row.createdBy?.name || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/warehousing/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Warehousing"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/warehousing/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Warehousing">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchWarehousing,
    { data: warehousingRes, isLoading, isFetching, error },
  ] = useLazyListWarehousingQuery();

  useEffect(() => {
    fetchWarehousing({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchWarehousing, keyword, perPage]);

  useAppMeta(`${activeTab} Warehousing`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={warehousingTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Warehousing ID/shipment ID"
          disabled={isLoading || isFetching}
        ></TableFilter>

        <AppTable
          columns={columns}
          data={warehousingRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={warehousingRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default WarehousingListScreen;

const PageActions = () => {
  const [openBulkRouteModal, setOpenBulkRouteModal] = useState(false);
  const { viewEntity } = useAuth();

  return (
    <Box display="flex" gap={2} alignItems="center">
      <Link to="/app/crm/warehousing/create">
        <Button startIcon={<Add />} variant="contained" color="primary">
          Add Warehousing
        </Button>
      </Link>

      <Button
        startIcon={<RouteSharp />}
        variant="contained"
        color="primary"
        onClick={() => setOpenBulkRouteModal(true)}
      >
        Bulk Assign Routes
      </Button>

      <TransitBulkRoutesModalForm
        open={openBulkRouteModal}
        onClose={() => setOpenBulkRouteModal(false)}
        accountId={viewEntity?.entity?.account?._id}
        entityId={viewEntity?.entity?._id}
      />
    </Box>
  );
};
